/**
 * Session Tracking for a module (a cohort's, or a 1:1 student's card): one
 * card per live class, numbered by class date, styled like the Task Lists
 * rows. A card opens the session page (recording, and later the session notes
 * and the AI chat); an upcoming class can be joined straight from its card.
 *
 * Mentors also schedule classes and add recordings of past ones from here.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Chip, LinearProgress, Snackbar, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import HistoryIcon from "@mui/icons-material/History";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import VideocamOffOutlinedIcon from "@mui/icons-material/VideocamOffOutlined";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import { formatClock } from "./SecurePlayer";
import { AddRecordingDialog, FONT, STATUS, ScheduleDialog, btn, fmtDay, fmtWhen } from "./sessionParts";
import { errorMessage, listSessions, scheduleSession } from "./sessionsApi";

const POLL_MS = 60000;

const ICONS = {
  ready: PlayCircleOutlineIcon,
  upcoming: EventOutlinedIcon,
  live: FiberManualRecordIcon,
  processing: HourglassTopIcon,
  awaiting_recording: HourglassTopIcon,
  failed: VideocamOffOutlinedIcon,
  no_recording: VideocamOffOutlinedIcon,
};

export const totalLength = (session) => session.recordings.reduce((n, r) => n + (r.duration_seconds || 0), 0);
export const watchedPercent = (session) => {
  const ready = session.recordings.filter((r) => r.status === "ready");
  return ready.length ? Math.round(ready.reduce((n, r) => n + (r.my_percent || 0), 0) / ready.length) : 0;
};

export function SessionCard({ session, editor, onOpen }) {
  const Icon = ICONS[session.status] || EventOutlinedIcon;
  const length = totalLength(session);
  const percent = watchedPercent(session);
  const upcoming = session.status === "upcoming" || session.status === "live";
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === "Enter") onOpen(); }}
      sx={{
        display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap",
        ml: { xs: 0, sm: 4 }, my: 1, px: 1.5, py: 1.1, width: { xs: "100%", sm: "calc(100% - 32px)" },
        backgroundColor: "#e3f0f9", border: "1px solid #9fcdea", borderRadius: "8px", cursor: "pointer",
        transition: "background-color .15s", "&:hover": { backgroundColor: "#d9ebf7" },
        "&:focus-visible": { outline: "2px solid #0172A6", outlineOffset: 2 },
      }}
    >
      <Icon sx={{ fontSize: 20, color: session.status === "live" ? "#dc2626" : "#0172A6" }} />
      <Box sx={{ flex: 1, minWidth: 180 }}>
        <Typography sx={{ fontSize: 15, fontFamily: FONT, color: "#202124" }}>
          <Box component="span" sx={{ fontWeight: 600 }}>Session {session.number}</Box>
          {session.title ? ` · ${session.title}` : ""}
        </Typography>
        <Typography sx={{ fontSize: 12.5, fontFamily: FONT, color: "#6b7280" }}>
          {upcoming && session.ends_at ? fmtWhen(session.starts_at) : fmtDay(session.starts_at)}
          {length > 0 && ` · ${formatClock(length)}`}
          {editor && session.viewers && ` · watched by ${session.viewers.watched}/${session.viewers.students}`}
          {session.shared_from && ` · shared from ${session.shared_from}'s card`}
        </Typography>
      </Box>
      {!editor && session.status === "ready" && percent > 0 && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, width: 110 }}>
          <LinearProgress variant="determinate" value={percent} sx={{ flex: 1, height: 5, borderRadius: 3 }} />
          <Typography sx={{ fontSize: 12, fontFamily: FONT, color: "#374151" }}>{percent}%</Typography>
        </Box>
      )}
      {session.can_join && session.meeting_link ? (
        <Button size="small" variant="contained" color="success" href={session.meeting_link} target="_blank"
          rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} sx={{ ...btn, boxShadow: "none" }}>
          Join
        </Button>
      ) : (
        <Chip size="small" label={STATUS[session.status]?.label} color={STATUS[session.status]?.color} variant="outlined"
          sx={{ backgroundColor: "#fff" }} />
      )}
      <ChevronRightIcon sx={{ color: "#5f6368" }} />
    </Box>
  );
}

/** kind "template": a cohort module (the batch is invited); "topic": a 1:1
 *  card (the student is invited). Same list, same buttons. */
export default function SessionTracking({ moduleId, basePath, kind = "template" }) {
  const oneToOne = kind === "topic";
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [dialog, setDialog] = useState(null); // schedule | past
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState("");

  const editor = !!data?.can_edit;
  const sessions = useMemo(() => data?.sessions || [], [data]);

  const load = useCallback(async () => {
    try {
      setData(await listSessions(moduleId, kind));
      setLoadError("");
    } catch (error) {
      setLoadError(errorMessage(error, "Couldn't load the sessions"));
    }
  }, [moduleId, kind]);

  useEffect(() => { load(); }, [load]);

  // Refresh while something changes on its own (a class starting, a recording processing).
  useEffect(() => {
    const changing = sessions.some((s) => ["upcoming", "live", "awaiting_recording", "processing"].includes(s.status));
    if (!changing) return undefined;
    const timer = setInterval(load, POLL_MS);
    return () => clearInterval(timer);
  }, [sessions, load]);

  const open = (session) => navigate(`${basePath}/${session.id}`);

  const schedule = async (form) => {
    setBusy(true);
    try {
      setData(await scheduleSession(moduleId, form, kind));
      setDialog(null);
      setToast(oneToOne ? "Scheduled. The student has been sent a Meet invite." : "Scheduled. The batch has been sent a Meet invite.");
    } catch (error) {
      setToast(errorMessage(error, "Couldn't schedule the session"));
    } finally {
      setBusy(false);
    }
  };

  if (loadError) {
    return (
      <Box sx={{ p: 1 }}>
        <Typography sx={{ color: "#b91c1c", fontSize: 14, fontFamily: FONT }}>{loadError}</Typography>
        <Button size="small" onClick={load} sx={btn}>Try again</Button>
      </Box>
    );
  }
  if (!data) return <LinearProgress sx={{ my: 1 }} />;

  return (
    <Box>
      {editor && (
        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", flexWrap: "wrap", mb: 0.5 }}>
          <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => setDialog("schedule")}
            sx={{ ...btn, backgroundColor: "#0172A6", boxShadow: "none" }}>
            Schedule session
          </Button>
          <Button size="small" variant="outlined" startIcon={<HistoryIcon />} onClick={() => setDialog("past")} sx={btn}>
            Add recording
          </Button>
        </Box>
      )}

      {sessions.length === 0 ? (
        <Box sx={{ ml: { xs: 0, sm: 4 }, my: 1, py: 2.5, px: 2, textAlign: "center", border: "1px dashed #c7d7e2", borderRadius: 2 }}>
          <Typography sx={{ fontSize: 14, fontFamily: FONT, color: "#374151" }}>No sessions yet</Typography>
          <Typography sx={{ fontSize: 13, fontFamily: FONT, color: "#6b7280", mt: 0.5 }}>
            {editor
              ? oneToOne
                ? "Schedule the next class (the student gets a Meet invite), or add the recording of a class that already happened. Sessions are numbered by date."
                : "Schedule the next class (everyone in the batch gets a Meet invite), or add the recording of a class that already happened. Sessions are numbered by date."
              : "Your mentor's classes for this module will appear here."}
          </Typography>
        </Box>
      ) : (
        sessions.map((s) => <SessionCard key={s.id} session={s} editor={editor} onOpen={() => open(s)} />)
      )}

      <ScheduleDialog open={dialog === "schedule"} mode="schedule" oneToOne={oneToOne} busy={busy} onClose={() => setDialog(null)} onSubmit={schedule} />
      <AddRecordingDialog open={dialog === "past"} moduleId={moduleId} kind={kind} onClose={() => setDialog(null)}
        onDone={(payload, _id, message) => { setData(payload); setDialog(null); setToast(message); }} />
      <Snackbar open={!!toast} autoHideDuration={5000} onClose={() => setToast("")} message={toast} />
    </Box>
  );
}
